import _debounce from 'lodash/debounce';
import {
    bool,
    number,
    string,
    array,
    object,
    oneOfType,
    func,
    arrayOf,
    shape,
    any,
} from 'prop-types';
import React, { useState } from 'react';
import ReactSelect, { Async } from 'react-select';

import DropdownIndicator from 'ravenjs/lib/Select/DropdownIndicator';
import getSelectStyles from 'ravenjs/lib/Select/SelectStyles';
import { isEmpty } from 'ravenjs/utils/lodash';
import { callFunc } from 'ravenjs/utils/actions';

import { DEFAULT_COLORS } from 'constants/colors';
import MultiSelectOption from './MultiSelectOption';
import MultiValueContainer from './MultiValueContainer';
import ValueContainer from './ValueContainer';

const DropdownFilter = props => {
    const {
        asyncCallback,
        id,
        isMulti,
        closeMenuOnSelect,
        isSearchable,
        name,
        onChange,
        onOpen,
        options,
        placeholder,
        value,
        isClearable,
        borderseparator,
        formatOptionLabel,
        isOptionDisabled,
        isOptionSelected,
        displayMultiValues,
        showRequired,
        hideSelectedCount,
        updateIcon,
    } = props;
    const [asyncOptions, setAsyncOptions] = React.useState([]);
    const [defaultAsyncOptions, setDefaultAsyncOptions] = React.useState([]);
    const allOption = options.find(({ isAllOption }) => isAllOption);
    const selectRef = React.createRef();

    const customStyles = {
        input: {
            minWidth: '50px',
        },
        option: {
            alignItems: 'center',
            display: 'flex',
            borderBottom: borderseparator ? `1px solid ${DEFAULT_COLORS.GREY_24}` : null,
        },
        ...props.customStyles,
    };
    const styles = getSelectStyles(props, customStyles, showRequired);

    const [isOpen, setIsOpen] = useState(false);

    const getComponents = () => {
        const components = { DropdownIndicator };
        if (isMulti) {
            components.MultiValue = displayMultiValues ? MultiValueContainer : () => null;
            components.Option = MultiSelectOption;
            components.ValueContainer = ValueContainer;
        }
        return components;
    };

    const getSelectedValue = () => {
        if (isMulti) {
            return isEmpty(value) ? [] : value;
        }
        const updatedOptions =
            asyncCallback && typeof asyncCallback === 'function' ? asyncOptions : options;
        return updatedOptions.find(option => option.value === value) || null;
    };

    const handleOnChange = changedObj => {
        let updatedValue;
        if (isMulti) {
            if (allOption && isAllOptionSelected(changedObj)) {
                updatedValue = [
                    {
                        label: allOption.label,
                        value: allOption.value,
                    },
                ];
            } else {
                updatedValue = changedObj;
            }
        } else {
            updatedValue = changedObj?.value;
        }
        if (closeMenuOnSelect) {
            setIsOpen(false);
            callFunc(onOpen, false);
        }
        callFunc(onChange, name, updatedValue);
    };

    const handleOnMouseEnter = () => {
        callFunc(onOpen, true);
        setIsOpen(true);
    };

    const handleOnMouseLeave = () => {
        callFunc(onOpen, false);
        setIsOpen(false);
    };

    const isAllOptionSelected = changedSelectedOptions => {
        return Boolean(changedSelectedOptions.find(({ value }) => value === allOption.value));
    };

    const loadOptions = _debounce((inputValue, callback) => {
        asyncCallback(inputValue)
            .then(updatedOptions => {
                if (isEmpty(defaultAsyncOptions)) {
                    setDefaultAsyncOptions(updatedOptions);
                }
                setAsyncOptions([...defaultAsyncOptions, ...updatedOptions]);
                return callback(updatedOptions);
            })
            .catch(() => {
                return callback([]);
            });
    }, 500);

    const selectProps = {
        hideSelectedOptions: false,
        id,
        isMulti,
        isSearchable,
        classNamePrefix: 'document-dropdown-filter',
        components: getComponents(),
        ref: selectRef,
        menuIsOpen: isOpen,
        onChange: handleOnChange,
        options,
        placeholder,
        styles,
        value: getSelectedValue(),
        isClearable,
        formatOptionLabel,
        isOptionDisabled,
        isOptionSelected,
        hideSelectedCount,
        updateIcon,
    };

    if (asyncCallback && typeof asyncCallback === 'function') {
        return (
            <div onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
                <Async {...selectProps} defaultOptions loadOptions={loadOptions} />
            </div>
        );
    }
    return (
        <div onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
            <ReactSelect {...selectProps} />
        </div>
    );
};

DropdownFilter.propTypes = {
    asyncCallback: func,
    id: oneOfType([number, string]).isRequired,
    isMulti: bool,
    closeMenuOnSelect: bool,
    isSearchable: bool,
    name: string.isRequired,
    onChange: func,
    onOpen: func,
    options: arrayOf(
        shape({
            isAllOption: bool,
            label: string,
            value: any.isRequired,
        })
    ),
    placeholder: string,
    value: oneOfType([number, string, array, object]),
    borderseparator: bool,
    isClearable: bool,
    formatOptionLabel: func,
    isOptionDisabled: func,
    isOptionSelected: func,
    showRequired: bool,
    hideSelectedCount: bool,
    updateIcon: bool,
    customStyles: object,
    displayMultiValues: bool,
};

DropdownFilter.defaultProps = {
    asyncCallback: null,
    isMulti: false,
    closeMenuOnSelect: true,
    isSearchable: false,
    onChange: () => {},
    onOpen: () => {},
    options: [],
    placeholder: null,
    value: [],
    borderseparator: false,
    isClearable: false,
    formatOptionLabel: null,
    isOptionDisabled: null,
    isOptionSelected: null,
    showRequired: false,
    hideSelectedCount: false,
    updateIcon: false,
    customStyles: {},
    displayMultiValues: false,
};

export default DropdownFilter;
